export const createWebSocketService = (driverId: string, token: string) => {
  const wsUrl = import.meta.env.VITE_WS_URL || "ws://localhost:8000";
  let ws: WebSocket | null = null;
  let isOnline = false;
  let onOnlineStatusCallback: ((isOnline: boolean) => void) | null = null;
  let onRequestCallback: ((request: any) => void) | null = null;
  let onRideProgressUpdate: ((update: any) => void) | null = null;
  let onRideAccepted: ((acceptedRideData?: any) => void) | null = null;
  let onRideCancelled: ((data?: any) => void) | null = null;
  let onRideAcceptedByOther: ((data: any) => void) | null = null;
  let onRideStatusUpdated: ((data: any) => void) | null = null;
  let onDriverOnlineStatus: ((data: any) => void) | null = null;
  let onBikePaired: ((data: any) => void) | null = null;
  let onBikeUnpaired: ((data: any) => void) | null = null;
  let onAnyMessage: (() => void) | null = null;
  let reconnectTimeout: NodeJS.Timeout | null = null;
  let intentionallyOffline = false;
  let connectionPromise: Promise<void> | null = null;
  let lastLocationUpdate = 0;
  const LOCATION_UPDATE_THROTTLE = 2000; // Minimum 2 seconds between location updates

  // Heartbeat
  let heartbeatInterval: NodeJS.Timeout | null = null;
  const HEARTBEAT_INTERVAL_MS = 25000; // Send heartbeat every 25s (server expects within 30s*3=90s)

  // Reconnection with exponential backoff
  let reconnectAttempts = 0;
  const MAX_RECONNECT_ATTEMPTS = 15;
  const BASE_RECONNECT_DELAY = 1000; // 1 second
  const MAX_RECONNECT_DELAY = 30000; // 30 seconds

  // Track if connect() has resolved/rejected to avoid double-firing
  let connectSettled = false;

  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatInterval = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "heartbeat" }));
      }
    }, HEARTBEAT_INTERVAL_MS);
  };

  const stopHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  };

  const getReconnectDelay = (): number => {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s, 30s, ...
    const delay = Math.min(
      BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts),
      MAX_RECONNECT_DELAY,
    );
    return delay;
  };

  const connect = (): Promise<void> => {
    if (intentionallyOffline) {
      console.log("Not connecting - user is intentionally offline");
      return Promise.reject(new Error("User is intentionally offline"));
    }

    // If already connecting, return the existing promise
    if (connectionPromise) {
      return connectionPromise;
    }

    connectSettled = false;

    connectionPromise = new Promise((resolve, reject) => {
      try {
        // Close any existing connection before creating a new one
        if (ws) {
          ws.onclose = null;
          ws.onerror = null;
          ws.onmessage = null;
          ws.onopen = null;
          try {
            ws.close();
          } catch (_) {
            /* ignore */
          }
          ws = null;
        }

        const url = `${wsUrl}/ws/driver/${driverId}?token=${encodeURIComponent(token)}`;
        console.log("🔌 Connecting to WebSocket:", url);
        ws = new WebSocket(url);

        const connectionTimeout = setTimeout(() => {
          if (!connectSettled) {
            connectSettled = true;
            connectionPromise = null;
            reject(new Error("WebSocket connection timeout"));
          }
        }, 10000);

        ws.onopen = () => {
          clearTimeout(connectionTimeout);
          if (connectSettled) return;
          connectSettled = true;

          console.log("✅ WebSocket connected successfully");
          isOnline = true;
          reconnectAttempts = 0; // Reset on successful connection
          onOnlineStatusCallback?.(true);
          onAnyMessage?.();
          connectionPromise = null;
          startHeartbeat();
          resolve();

          // Send current location if available
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const location = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                };
                console.log(
                  "📍 Sending initial location on connection:",
                  location,
                );

                // Use a longer delay to ensure WebSocket is fully ready
                setTimeout(() => {
                  updateLocation(location);
                }, 500);
              },
              (error) => {
                console.error("Error getting initial location:", error);

                // Try again with lower accuracy if high accuracy fails
                if (error.code === 3) {
                  // TIMEOUT
                  console.log(
                    "High accuracy timeout, trying with lower accuracy...",
                  );
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      const location = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                      };
                      console.log(
                        "📍 Sending initial location (low accuracy):",
                        location,
                      );

                      setTimeout(() => {
                        updateLocation(location);
                      }, 500);
                    },
                    (retryError) => {
                      console.error(
                        "Failed to get location even with low accuracy:",
                        retryError,
                      );
                    },
                    {
                      enableHighAccuracy: false,
                      timeout: 90000,
                      maximumAge: 60000,
                    },
                  );
                }
              },
              {
                enableHighAccuracy: true,
                timeout: 60000,
                maximumAge: Infinity,
              },
            );
          }
        };

        ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          console.log("WebSocket disconnected", event.code, event.reason);
          isOnline = false;
          stopHeartbeat();
          onOnlineStatusCallback?.(false);
          connectionPromise = null;

          // Only reject if onopen never fired (connection failed)
          if (!connectSettled) {
            connectSettled = true;
            reject(
              new Error(`WebSocket closed before connecting: ${event.code}`),
            );
          }

          if (!intentionallyOffline) {
            scheduleReconnect();
          }
        };

        ws.onerror = (error) => {
          clearTimeout(connectionTimeout);
          console.error("WebSocket error:", error);
          // Don't reject here - let onclose handle it to avoid double-firing
          // onclose is always called after onerror
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log("📨 Received WebSocket message:", data);

            if (data.type === "QR_RIDE_STARTED") {
              console.log("🚲 QR ride started (ARRIVED):", data.data);
              onRideAccepted?.(data.data);
            } else if (data.type === "ride_request") {
              console.log("🚗 Received ride request:", data.data);
              onRequestCallback?.(data);
            } else if (data.type === "RIDE_PROGRESS_UPDATE") {
              onRideProgressUpdate?.(data.data);
            } else if (
              data.type === "RIDE_ACCEPTANCE_RESPONSE" &&
              data.success
            ) {
              console.log("✅ Ride acceptance confirmed:", data);
              if (data.data) {
                console.log(
                  "🚗 Received accepted ride request data:",
                  data.data,
                );
                onRequestCallback?.({ type: "ride_request", data: data.data });
              }
              const acceptedData = data.data || {
                request_id: data.request_id,
                status: data.status,
                ride_type: data.ride_type,
              };
              //

              // Only fire callback if we have a request_id to avoid overwriting existing ride data with undefined
              if (acceptedData.request_id) {
                onRideAccepted?.(acceptedData);
              } else {
                console.log(
                  "⚠️ Ignoring RIDE_ACCEPTANCE_RESPONSE without request_id",
                );
              }
            } else if (
              data.type === "RIDE_ACCEPTANCE_RESPONSE" &&
              data?.success === false
            ) {
              console.log("❌ Ride acceptance failed:", data);
              onRideCancelled?.();
            } else if (
              data.type === "RIDE_CANCELLATION_RESPONSE" &&
              data.success
            ) {
              console.log("✅ Driver ride cancellation confirmed:", data);
              // Don't call onRideCancelled for driver-initiated cancellations
              // The cancelRide function in AppContext already handles the state
            } else if (data.type === "DRIVER_ARRIVED_RESPONSE") {
              console.log("🎯 Driver arrived response received:", data);
            } else if (data.type === "RIDE_COMPLETION_RESPONSE") {
              console.log("✅ Ride completion response received:", data);
            } else if (data.type === "RIDE_CANCELLED") {
              console.log("🚫 Client cancelled the ride:", data);
              onRideCancelled?.(data);
            } else if (data.type === "RIDE_ACCEPTED_BY_OTHER") {
              console.log("👥 Ride was accepted by another driver:", data);
              onRideAcceptedByOther?.(data);
            } else if (data.type === "REQUEST_EXPIRED") {
              console.log("⏰ Ride request expired:", data);
              onRideAcceptedByOther?.(data);
            } else if (data.type === "ride_status_updated") {
              console.log("🔄 Ride status updated:", data);
              onRideStatusUpdated?.(data);
            } else if (data.type === "DRIVER_ONLINE_STATUS") {
              console.log("🟢 Driver online status from backend:", data.data);
              onDriverOnlineStatus?.(data.data);
            } else if (data.type === "BIKE_PAIRED") {
              console.log("🔗 Bike paired:", data.data);
              onBikePaired?.(data.data);
            } else if (data.type === "BIKE_UNPAIRED") {
              console.log("🔓 Bike unpaired:", data.message);
              onBikeUnpaired?.(data);
            } else if (data.type === "heartbeat") {
              // Server heartbeat acknowledged silently
            } else {
              console.log("📨 Other message type:", data.type);
            }
          } catch (error) {
            console.error("Error parsing WebSocket message:", error);
          }
        };
      } catch (error) {
        connectionPromise = null;
        if (!connectSettled) {
          connectSettled = true;
          reject(error);
        }
      }
    });

    return connectionPromise;
  };

  const scheduleReconnect = () => {
    if (intentionallyOffline) {
      console.log("Not scheduling reconnect - user is intentionally offline");
      return;
    }

    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.log(
        `Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Call goOnline() to retry.`,
      );
      return;
    }

    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
    }

    const delay = getReconnectDelay();
    reconnectAttempts++;
    console.log(
      `Scheduling reconnect attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`,
    );

    reconnectTimeout = setTimeout(() => {
      console.log("Attempting to reconnect...");
      connect().catch(() => {
        // Error handled by onclose -> scheduleReconnect
      });
    }, delay);
  };

  const updateLocation = async (location: { lat: number; lng: number }) => {
    const now = Date.now();

    if (now - lastLocationUpdate < LOCATION_UPDATE_THROTTLE) {
      return;
    }

    if (ws?.readyState === WebSocket.OPEN) {
      const message = {
        type: "location_update",
        data: {
          latitude: location.lat,
          longitude: location.lng,
        },
      };
      console.log("📍 Sending location update:", message);
      ws.send(JSON.stringify(message));
      console.log("✅ Location update sent successfully");
      lastLocationUpdate = now;
    } else {
      console.log("⚠️ WebSocket not open, attempting to connect...");

      try {
        // Try to establish connection if not connected
        if (!ws || ws.readyState === WebSocket.CLOSED) {
          await connect();
        }

        // Wait a bit for the connection to stabilize
        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (ws?.readyState === WebSocket.OPEN) {
          const message = {
            type: "location_update",
            data: {
              latitude: location.lat,
              longitude: location.lng,
            },
          };
          console.log("📍 Sending location update after connection:", message);
          ws.send(JSON.stringify(message));
          console.log("✅ Location update sent successfully after connection");
          lastLocationUpdate = now;
        } else {
          console.log("❌ WebSocket still not open after connection attempt");
        }
      } catch (error) {
        console.error("❌ Failed to connect for location update:", error);
      }
    }
  };

  const acceptRequest = async (requestId: string) => {
    if (ws?.readyState === WebSocket.OPEN) {
      const message = {
        type: "accept_request",
        data: {
          request_id: requestId,
        },
      };
      console.log("Accepting request:", message);
      ws.send(JSON.stringify(message));
    } else {
      throw new Error("WebSocket not open, cannot accept request");
    }
  };

  const declineRequest = async (requestId: string) => {
    if (ws?.readyState === WebSocket.OPEN) {
      const message = {
        type: "decline_request",
        data: {
          request_id: requestId,
        },
      };
      console.log("Declining request:", message);
      ws.send(JSON.stringify(message));
    } else {
      throw new Error("WebSocket not open, cannot decline request");
    }
  };

  const cancelRide = async (requestId: string) => {
    if (ws?.readyState === WebSocket.OPEN) {
      const message = {
        type: "cancel_ride",
        data: {
          request_id: requestId,
        },
      };
      console.log("Cancelling ride:", message);
      ws.send(JSON.stringify(message));
    } else {
      throw new Error("WebSocket not open, cannot cancel ride");
    }
  };

  const goOnline = async () => {
    intentionallyOffline = false;
    reconnectAttempts = 0; // Reset attempts when user explicitly goes online
    console.log("🚀 goOnline called, checking WebSocket state...");

    if (ws?.readyState === WebSocket.OPEN) {
      console.log("✅ WebSocket already connected, setting online status");
      isOnline = true;
      onOnlineStatusCallback?.(true);
      startHeartbeat();
      console.log("✅ Driver marked as online, ready to receive requests");
      return;
    }

    console.log("🔌 WebSocket not connected, establishing connection...");
    try {
      await connect();
      console.log("✅ WebSocket connection established successfully");
      isOnline = true;
      onOnlineStatusCallback?.(true);
      console.log("✅ Driver connected and online, ready to receive requests");
    } catch (error) {
      console.error("❌ Failed to establish WebSocket connection:", error);
      throw error;
    }
  };

  const goOffline = () => {
    intentionallyOffline = true;
    stopHeartbeat();
    isOnline = false;
    onOnlineStatusCallback?.(false);
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "go_offline" }));
      // Give server time to process go_offline before closing the connection
      setTimeout(() => {
        disconnect();
      }, 300);
    } else {
      disconnect();
    }
  };

  const disconnect = () => {
    stopHeartbeat();
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
    if (ws) {
      ws.onclose = null; // Prevent reconnect on intentional disconnect
      ws.onerror = null;
      ws.close();
      ws = null;
    }
    connectionPromise = null;
  };

  const setOnOnlineStatusCallback = (callback: (isOnline: boolean) => void) => {
    onOnlineStatusCallback = callback;
  };

  const setOnRequestCallback = (callback: (request: any) => void) => {
    onRequestCallback = callback;
  };

  const setOnRideAcceptedCallback = (
    callback: (acceptedRideData?: any) => void,
  ) => {
    onRideAccepted = callback;
  };

  const setOnRideProgressUpdateCallback = (callback: (update: any) => void) => {
    onRideProgressUpdate = callback;
  };

  const setOnRideCancelledCallback = (callback: (data?: any) => void) => {
    onRideCancelled = callback;
  };

  const setOnRideAcceptedByOtherCallback = (callback: (data: any) => void) => {
    onRideAcceptedByOther = callback;
  };

  const setOnRideStatusUpdatedCallback = (callback: (data: any) => void) => {
    onRideStatusUpdated = callback;
  };

  const setOnDriverOnlineStatusCallback = (callback: (data: any) => void) => {
    onDriverOnlineStatus = callback;
  };

  const setOnBikePairedCallback = (callback: (data: any) => void) => {
    onBikePaired = callback;
  };

  const setOnBikeUnpairedCallback = (callback: (data: any) => void) => {
    onBikeUnpaired = callback;
  };

  const setOnAnyMessageCallback = (callback: () => void) => {
    onAnyMessage = callback;
  };

  const sendMessage = (message: any) => {
    if (ws?.readyState === WebSocket.OPEN) {
      console.log("📤 Sending WebSocket message:", message);
      ws.send(JSON.stringify(message));
      return true;
    } else {
      console.error("❌ WebSocket not open, cannot send message");
      return false;
    }
  };

  return {
    goOnline,
    goOffline,
    disconnect,
    updateLocation,
    acceptRequest,
    declineRequest,
    cancelRide,
    sendMessage,
    setOnOnlineStatusCallback,
    setOnRequestCallback,
    setOnRideAcceptedCallback,
    setOnRideProgressUpdateCallback,
    setOnRideCancelledCallback,
    setOnRideAcceptedByOtherCallback,
    setOnRideStatusUpdatedCallback,
    setOnDriverOnlineStatusCallback,
    setOnBikePairedCallback,
    setOnBikeUnpairedCallback,
    setOnAnyMessageCallback,
  };
};
